import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumb, BackdropCircular } from 'components';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  Card,
  Divider,
  Icon,
  Grid,
  FormControl,
  MenuItem,
  Typography,
  Switch,
  TextField
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  getHomepageAdditionalHeaderConfigsApi,
  saveHomepageAdditionalHeaderConfigsApi
} from 'api';


const additionalHeaderGroup = [
  {
    pathName: 'first',
    icon: 'c-icon-box',
    name: 'Box Icon'
  },
  {
    pathName: 'second',
    icon: 'c-icon-gift-box',
    name: 'Gift Box Icon'
  },
  {
    pathName: 'third',
    icon: 'c-icon-replenishment',
    name: 'Replenishment Icon'
  },
  {
    pathName: 'fourth',
    icon: 'c-icon-check',
    name: 'Check Icon'
  }
];

const additionalHeader = () => {
  const dispatch = useDispatch();
  const {
    homepageAdditionalHeaderConfigs,
    homepageAdditionalHeaderConfigs: {general},
    loadingHomepageAdditionalHeaderConfigs,
    savingHomepageAdditionalHeaderConfigs
  } = useSelector((state) => state.store_configs);

  const loading = loadingHomepageAdditionalHeaderConfigs ||
    savingHomepageAdditionalHeaderConfigs;

  useEffect(() => {
    if (!Object.keys(general)?.length) {
      getHomepageAdditionalHeaderConfigsApi({dispatch});
    }
  }, []);

  const handleSave = (values) => {
    const configs = Object.values(values).reduce((acc, value) => {
      for (const key in value) {
        acc = [
          ...acc,
          {
            path: key,
            value: value[key]
          }
        ];
      }

      return acc;
    }, []);
    saveHomepageAdditionalHeaderConfigsApi({dispatch, configs});
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y">
      <Breadcrumb
        routeSegments={[
          { name: 'Settings', path: '/settings#go_live_requirements' },
          { name: 'UI Configurations', path: '/settings#UI_configurations' },
          { name: 'Homepage Settings', path: '/configurations/homepage'},
          { name: 'Additional Header'}
        ]}
      />
      <div className="m-10">
        {!!Object.keys(general)?.length &&
          <Formik
            onSubmit={handleSave}
            initialValues={{
              ...homepageAdditionalHeaderConfigs
            }}
            validationSchema={yup.object({
              general: yup.object({
                'store_info/premium_plus_additional_header/is_additional_header_enable': yup
                  .string()
              }),
              first: yup.object().shape({
                'store_info/premium_plus_additional_header/additional_header_first_item/additional_header_first_title': yup
                  .string()
                  .ensure()
                  .test(
                    'first',
                    'Field is required',
                    (val, values) => {
                      const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_additional_header/is_additional_header_enable'];
                      return isBlockEnable && Boolean(val);
                    }
                  )
              }),
              second: yup.object().shape({
                'store_info/premium_plus_additional_header/additional_header_second_item/additional_header_second_title': yup
                  .string()
                  .ensure()
                  .test(
                    'second',
                    'Field is required',
                    (val, values) => {
                      const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_additional_header/is_additional_header_enable'];
                      return isBlockEnable && Boolean(val);
                    }
                  )
              }),
              third: yup.object().shape({
                'store_info/premium_plus_additional_header/additional_header_third_item/additional_header_third_title': yup
                  .string()
                  .ensure()
                  .test(
                    'third',
                    'Field is required',
                    (val, values) => {
                      const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_additional_header/is_additional_header_enable'];
                      return isBlockEnable && Boolean(val);
                    }
                  )
              }),
              fourth: yup.object().shape({
                'store_info/premium_plus_additional_header/additional_header_fourth_item/additional_header_fourth_title': yup
                  .string()
                  .ensure()
                  .test(
                    'fourth',
                    'Field is required',
                    (val, values) => {
                      const isBlockEnable = values?.from[1].value.general['store_info/premium_plus_additional_header/is_additional_header_enable'];
                      return isBlockEnable && Boolean(val);
                    }
                  )
              })
            })}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleChange,
              handleBlur,
              handleSubmit
            }) => (
              <form autoComplete="off" onSubmit={handleSubmit}>
                <Card className="p-8">
                  <Typography variant="h4" className="mb-8">Additional Header</Typography>
                  <Divider className="mb-8" />
                  <Grid container spacing={3} className="mb-8">
                    <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
                    <Grid item xs={12} sm={9}>
                      <Switch
                        color="primary"
                        name={'general[\'store_info/premium_plus_additional_header/is_additional_header_enable\']'}
                        checked={values.general['store_info/premium_plus_additional_header/is_additional_header_enable'] == 1}
                        onChange={(event) => setFieldValue(
                          'general[\'store_info/premium_plus_additional_header/is_additional_header_enable\']',
                          event.target.checked ? '1' : '0'
                        )}
                      />
                    </Grid>
                  </Grid>
                  {additionalHeaderGroup.map((item) => {
                    const isTouchedTitle = touched?.[item.pathName]?.[`store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_title`];
                    const titleErrorMessage = errors?.[item.pathName]?.[`store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_title`];
                    return (
                      <div  key={item.pathName}>
                        <Accordion elevation={0}>
                          <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                            {Boolean(titleErrorMessage) ?
                              <Badge badgeContent={'!'} color="error">
                                <Typography variant="h3" className="mb-4 capitalize">
                                  {item.pathName} Additional Header:
                                </Typography>
                              </Badge>
                              :
                              <Typography variant="h3" className="my-4 capitalize">
                                {item.pathName} Additional Header:
                              </Typography>
                            }
                          </AccordionSummary>
                          <AccordionDetails className="flex-column">
                            <Grid container spacing={3} className="mb-8">
                              <Grid item xs={12} sm={2} className="ml-4">Content<span className="text-error">*</span></Grid>
                              <Grid item xs={12} sm={9}>
                                <TextField
                                  fullWidth
                                  required
                                  multiline
                                  minRows={3}
                                  variant="outlined"
                                  label="Text"
                                  name={`[${item.pathName}].[store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_title]`}
                                  value={values[item.pathName]?.[`store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_title`]}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  inputProps={{
                                    className: 'p-0',
                                    shrink: 'true',
                                    maxLength: 40
                                  }}
                                  helperText={
                                    (isTouchedTitle && Boolean(titleErrorMessage))
                                      ? titleErrorMessage
                                      :
                                      <div className="flex items-center">
                                        <Icon>arrow_drop_up</Icon>
                                        {40 - values[item.pathName]?.[`store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_title`].length}
                                        {' characters remaining'}
                                      </div>
                                  }
                                  error={isTouchedTitle && Boolean(titleErrorMessage)}
                                />
                              </Grid>
                              <Grid item xs={12} sm={2} className="ml-4">Select Icon</Grid>
                              <Grid item xs={12} sm={9}>
                                <FormControl fullWidth>
                                  <TextField
                                    label="Icon"
                                    variant="outlined"
                                    select
                                    name={`[${item.pathName}].[store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_icon]`}
                                    value={values[item.pathName]?.[`store_info/premium_plus_additional_header/additional_header_${item.pathName}_item/additional_header_${item.pathName}_icon`]}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    inputProps={{
                                      type: 'option',
                                    }}
                                  >
                                    {additionalHeaderGroup.map((item, index) => (
                                      <MenuItem
                                        key={index}
                                        value={item.icon}
                                      >
                                        {item.name}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </div>
                    );
                  })}
                </Card>
                <div className="flex justify-end">
                  <Button
                    variant="contained"
                    color="primary"
                    className="mt-10 mb-5"
                    type="submit"
                    disabled={!!Object.keys(errors).length}
                  >
                    Save Configurations
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        }
      </div>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default additionalHeader;
